import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { exportNoteContent, type ExportFormat } from "@/lib/export";
import { canUseExportFormat } from "@/lib/pro";
import { useIsPro } from "@/store/subscription";
import { useUIStore } from "@/store/ui";
import { ProTag } from "@/components/ui/ProTag";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getCachedSharedNote, removeShared } from "../lib/services/sharedNoteService";
import { useAuthStore } from "../store/auth";
import { Icon } from "@/components/ui/Icon";

const EXPORT_FORMATS: {
  format: ExportFormat;
  labelKey: string;
  separatorBefore?: boolean;
}[] = [
  { format: "markdown", labelKey: "noteActions.exportMarkdown" },
  { format: "txt", labelKey: "noteActions.exportPlainText" },
  { format: "textpack", labelKey: "noteActions.exportTextpack" },
  { format: "html", labelKey: "noteActions.exportHtml", separatorBefore: true },
  { format: "rtf", labelKey: "noteActions.exportRtf" },
  { format: "epub", labelKey: "noteActions.exportEpub" },
];

interface SharedNoteActionsMenuProps {
  noteId: string;
  /** Called after the note is removed from the user's shared list. */
  onRemoved?: () => void;
}

/**
 * Limited action set for a note shared with the user by someone else.
 * Read-only: copy markdown, copy the public link, export, remove from list.
 * No pin/tag/archive/delete — the user does not own the note.
 * Must be used inside a DropdownMenuContent.
 */
export function SharedNoteActionsMenu({ noteId, onRemoved }: SharedNoteActionsMenuProps) {
  const { t } = useTranslation();
  const isPro = useIsPro();
  const openPaywall = useUIStore((s) => s.openPaywall);
  const userId = useAuthStore((s) => s.user?.id);

  const shareUrl = `${import.meta.env.VITE_WEB_URL ?? "https://caliuapp.com"}/share/${noteId}`;

  const handleCopyMarkdown = async () => {
    const cached = getCachedSharedNote(noteId);
    if (!cached) {
      toast.error(t("sharedView.notLoadedYet"));
      return;
    }
    try {
      await navigator.clipboard.writeText(cached.content);
      toast.success(t("sharedView.markdownCopied"));
    } catch {
      toast.error(t("sharedView.copyFailed"));
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("sharedView.linkCopied"));
    } catch {
      toast.error(t("sharedView.copyFailed"));
    }
  };

  const handleExport = async (format: ExportFormat) => {
    if (!canUseExportFormat(isPro, format)) {
      openPaywall("export");
      return;
    }
    const cached = getCachedSharedNote(noteId);
    if (!cached) {
      toast.error(t("sharedView.notLoadedYet"));
      return;
    }
    try {
      await exportNoteContent(cached.content, cached.title, format);
    } catch {
      toast.error(t("noteActions.exportFailed"));
    }
  };

  const handleRemove = () => {
    if (!userId) return;
    removeShared(noteId, userId);
    onRemoved?.();
  };

  return (
    <>
      <DropdownMenuItem onSelect={() => void handleCopyMarkdown()}>
        <Icon name="duplicate" size={15} />
        {t("sharedView.copyMarkdown")}
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={() => void handleCopyLink()}>
        <Icon name="link" size={15} />
        {t("noteActions.copyLink")}
      </DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Icon name="file-down" size={15} />
          {t("noteActions.exportAs")}
          <Icon name="chevron-right" size={13} className="ml-auto opacity-50" />
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {EXPORT_FORMATS.map((item) => (
            <Fragment key={item.format}>
              {item.separatorBefore && <DropdownMenuSeparator />}
              <DropdownMenuItem onSelect={() => void handleExport(item.format)}>
                {t(item.labelKey)}
                {!canUseExportFormat(isPro, item.format) && (
                  <ProTag className="ml-auto" />
                )}
              </DropdownMenuItem>
            </Fragment>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onSelect={handleRemove}
        className="text-danger data-[highlighted]:text-danger"
      >
        <Icon name="trash" size={15} />
        {t("sharedWithMe.removeFromList")}
      </DropdownMenuItem>
    </>
  );
}
